import React, { useState, useEffect, useMemo } from 'react';
import { Globe, Search, Loader2, AlertCircle, CheckCircle, Clock, XCircle, RefreshCw, ExternalLink, ChevronDown, ChevronRight, Copy, Info, Trash2 } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useFeatures } from '../../hooks/useFeatures';
import { apiEndpoints } from '../../utils/api';
import { startCheckout } from '../../lib/paddle';
import FeatureUpgradePrompt from '../common/FeatureUpgradePrompt';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import DnsRecordsPanel from './DnsRecordsPanel';
import { friendlyDomainError } from '../../utils/domainErrors';
import toast from 'react-hot-toast';

/**
 * Validates a fully qualified domain name (FQDN)
 * Supports: subdomains, internationalized domains, punycode, complex TLDs (.co.uk)
 */
function isFQDN(domain: string): boolean {
  if (typeof domain !== 'string' || domain.length > 253) {
    return false;
  }

  // Remove optional trailing dot
  const str = domain.endsWith('.') ? domain.slice(0, -1) : domain;

  // Comprehensive FQDN regex:
  // - Each label: 1-63 chars, alphanumeric/unicode, can contain hyphens (not at start/end)
  // - TLD: 2+ chars, letters only (or punycode starting with xn--)
  // - Supports multiple levels: subdomain.domain.tld or domain.co.uk
  const fqdnRegex = /^(?=.{1,253}$)(?:(?!-)[a-zA-Z0-9-]{1,63}(?<!-)\.)+[a-zA-Z]{2,}$/;
  return fqdnRegex.test(str);
}

function validateDomain(domain: string): { isValid: boolean; error?: string } {
  const trimmed = domain.trim();
  
  if (!trimmed) {
    return { isValid: false, error: 'Domain name is required' };
  }
  
  if (trimmed.length > 253) {
    return { isValid: false, error: 'Domain name is too long (max 253 characters)' };
  }
  
  if (!isFQDN(trimmed)) {
    return { isValid: false, error: 'Please enter a valid domain name (e.g., example.com)' };
  }
  
  return { isValid: true };
}

// --- TYPE DEFINITIONS ---
interface DomainAvailability {
  domain_name: string;
  available: boolean;
  price?: number;
  currency?: string;
  availability?: string;
  is_premium: boolean;
}

interface DomainSearchResponse {
  query: string;
  exact_match?: DomainAvailability;
  suggestions: DomainAvailability[];
  total: number;
  skip: number;
  limit: number;
}

interface Domain {
  id: string;
  user_id: string;
  domain_name: string;
  domain_type?: 'registered' | 'byod';
  status: 'payment_pending' | 'pending' | 'purchasing' | 'registered' | 'awaiting_dns_verification' | 'failed' | 'expired' | 'expiring_soon' | 'payment_failed' | 'deleting' | 'deleted' | 'deletion_failed';
  route53_operation_id?: string;
  hosted_zone_id?: string;
  name_servers?: string[];
  dns_verification_attempts?: number;
  dns_verification_started_at?: string;
  certificate_arn?: string;
  certificate_status?: 'not_requested' | 'pending_validation' | 'issued' | 'failed' | 'expired';
  certificate_error_message?: string;
  deletion_error?: string;
  certificate_issued_at?: string;
  cloudfront_distribution_id?: string;
  cloudfront_domain?: string;
  cloudfront_status?: 'pending' | 'creating' | 'deployed' | 'updating' | 'failed' | 'update_failed';
  cloudfront_status_message?: string;
  associated_workspace_id?: string;
  associated_workspace?: { id: string; name: string };
  created_at: string;
  updated_at: string;
  purchased_at?: string;
  expires_at?: string;
  purchase_price?: number;
  renewal_price?: number;
  currency: string;
  error_message?: string;
  last_assignment_change?: string;
}

interface DomainListResponse {
  domains: Domain[];
  total: number;
  skip: number;
  limit: number;
}

type DomainStatusGroup = 'action_required' | 'ready' | 'in_progress' | 'failed';

interface DomainStatusInfo {
  group: DomainStatusGroup;
  icon: React.ReactNode;
  text: string;
  color: string;
  description: string;
}

// --- HELPER & UI COMPONENTS ---

const Card = ({ children, className }: { children: React.ReactNode, className?: string }) => (
  <div className={`bg-white dark:bg-neutral-800/50 rounded-xl border border-neutral-200 dark:border-neutral-700/60 shadow-sm ${className}`}>
    {children}
  </div>
);

const InfoMessage = ({ message, type }: { message: string, type: 'error' | 'success' }) => {
  const baseClasses = "mt-4 p-3 rounded-lg flex items-center gap-3 text-sm";
  const typeClasses = {
    error: "bg-semantic-error/10 border border-semantic-error/30 text-semantic-error",
    success: "bg-semantic-success/10 border border-semantic-success/30 text-semantic-success"
  };
  const Icon = type === 'error' ? AlertCircle : CheckCircle;

  return (
    <div className={`${baseClasses} ${typeClasses[type]}`}>
      <Icon className="w-5 h-5 flex-shrink-0" />
      <p>{message}</p>
    </div>
  );
};

export const getDomainStatusInfo = (domain: Domain): DomainStatusInfo => {
  if (domain.status === 'deleting') {
    // group 'in_progress' is load-bearing: the 15s refresh poll keys off exactly this,
    // so the row updates itself through the teardown with no extra code.
    return {
      group: 'in_progress',
      icon: <Trash2 className="w-4 h-4 text-semantic-error" />,
      text: 'Deleting',
      color: 'text-semantic-error',
      description: "We're removing this domain's CDN, SSL certificate and DNS zone. This usually takes 15\u201320 minutes; the domain disappears from this list when it's done."
    };
  }

  if (domain.status === 'deletion_failed') {
    return {
      group: 'failed',
      icon: <XCircle className="w-4 h-4 text-semantic-error" />,
      text: 'Deletion Failed',
      color: 'text-semantic-error',
      description: domain.deletion_error
        ? `We couldn't finish removing this domain: ${domain.deletion_error}. Try deleting it again, or contact support.`
        : "We couldn't finish removing this domain. Try deleting it again, or contact support."
    };
  }

  if (domain.status === 'deleted') {
    // Defensive only: /domains/mine excludes deleted domains. 'ready' is the one group
    // with no retry button and no poll.
    return {
      group: 'ready',
      icon: <Trash2 className="w-4 h-4 text-neutral-500" />,
      text: 'Deleted',
      color: 'text-neutral-500',
      description: 'This domain has been deleted.'
    };
  }

  if (domain.status === 'registered' && domain.certificate_status === 'issued' && domain.cloudfront_status === 'deployed') {
    return {
      group: 'ready',
      icon: <CheckCircle className="w-4 h-4 text-semantic-success" />,
      text: 'Ready',
      color: 'text-semantic-success',
      description: 'Domain is configured and ready to use.'
    };
  }

  // Registered + secured, but the CDN is still deploying: the last step before Ready.
  if (
    domain.status === 'registered' &&
    domain.certificate_status === 'issued' &&
    domain.cloudfront_distribution_id &&
    (domain.cloudfront_status === 'pending' || domain.cloudfront_status === 'creating')
  ) {
    return {
      group: 'in_progress',
      icon: <Clock className="w-4 h-4 text-brand-primary" />,
      text: 'Finalizing',
      color: 'text-brand-primary',
      description: "Your domain is registered and secured. We're deploying its CDN configuration — usually ready in 10–15 minutes."
    };
  }

  if (
    domain.status === 'registered' &&
    domain.certificate_status === 'issued' &&
    domain.cloudfront_status === 'updating'
  ) {
    return {
      group: 'in_progress',
      icon: <Clock className="w-4 h-4 text-brand-primary" />,
      text: 'Updating',
      color: 'text-brand-primary',
      description: 'Applying an assignment change. This usually takes up to 15 minutes.'
    };
  }

  // The CDN step failed. Without this the row matches nothing below and falls through to
  // the "Setting Up" default, which spins forever: no error, no Retry button (isActionable
  // keys off group 'failed'), and a 15s poll that never stops. Group 'failed' fixes all
  // three. Deliberately below the deletion branches so a teardown still reads as Deleting.
  if (domain.cloudfront_status === 'failed' || domain.cloudfront_status === 'update_failed') {
    return {
      group: 'failed',
      icon: <XCircle className="w-4 h-4 text-semantic-error" />,
      text: 'Setup Failed',
      color: 'text-semantic-error',
      // The raw cloudfront_status_message ("CNAMEAlreadyExists: One or more of the CNAMEs
      // you provided...") is for logs and support, not for this sentence.
      description: "We couldn't finish setting up this domain. Try Retry Setup, or contact support if it keeps failing."
    };
  }

  if (domain.status === 'failed' || domain.status === 'payment_failed' || domain.status === 'expired') {
    let text = 'Failed';
    let description = domain.error_message || 'An unknown error occurred. Try reconciling.';
    if (domain.status === 'payment_failed') {
      text = 'Payment Failed';
      description = 'Payment for this domain failed. Please try purchasing again.';
    } else if (domain.status === 'expired') {
      text = 'Expired';
      description = 'This domain has expired and is no longer active.';
    }
    return { 
      group: 'failed',
      icon: <XCircle className="w-4 h-4 text-semantic-error" />,
      text,
      color: 'text-semantic-error',
      description
    };
  }

  if (domain.status === 'awaiting_dns_verification' || domain.status === 'payment_pending') {
    let text = 'Action Required';
    let description = 'Further action is needed to complete the setup.';
    if (domain.status === 'awaiting_dns_verification') {
      text = 'DNS Setup Required';
      // The backend stores a specific issue (e.g. a nameserver misconfiguration it
      // detected during verification) in error_message — show it over the generic hint.
      description = domain.error_message || 'Please update the nameservers at your domain registrar with the details below.';
    } else if (domain.status === 'payment_pending') {
      text = 'Payment Pending';
      description = 'Complete your payment to activate this domain.';
    }
    return { 
      group: 'action_required',
      icon: <AlertCircle className="w-4 h-4 text-semantic-warning" />,
      text,
      color: 'text-semantic-warning',
      description
    };
  }

  return { 
    group: 'in_progress',
    icon: <Clock className="w-4 h-4 text-brand-primary" />,
    text: 'Setting Up',
    color: 'text-brand-primary',
    description: 'We are setting up your domain. This can take from a few minutes up to 48 hours to complete.'
  };
};

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    // Optional: show a toast notification for feedback
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
  }
};

// --- MAIN COMPONENT ---

const DomainsSection = () => {
  const { has, loading: featuresLoading } = useFeatures();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DomainSearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [byodError, setByodError] = useState<string | null>(null);
  
  const [myDomains, setMyDomains] = useState<Domain[]>([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [domainsError, setDomainsError] = useState<string | null>(null);
  
  const [actionState, setActionState] = useState<{ [key: string]: boolean }>({});
  const [confirmState, setConfirmState] = useState<string | null>(null);
  
  const [purchaseSuccess, setPurchaseSuccess] = useState<string | null>(null);
  
  const [byodDomain, setBYODDomain] = useState('');
  const [byodValidationError, setByodValidationError] = useState<string | null>(null);
  
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const [initialExpansionDone, setInitialExpansionDone] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Domain | null>(null);

  const api = useAuthenticatedApi();

  // Check if user has access to custom domains feature
  const hasCustomDomainsFeature = has('custom-domains');

  // --- DATA FETCHING & EFFECTS ---

  useEffect(() => {
    if (api && !featuresLoading && hasCustomDomainsFeature) {
      fetchMyDomains();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, featuresLoading, hasCustomDomainsFeature]);

  useEffect(() => {
    const hasPendingDomains = myDomains.some(d => {
      const statusInfo = getDomainStatusInfo(d);
      return statusInfo.group === 'in_progress';
    });

    if (hasPendingDomains && api && !featuresLoading && hasCustomDomainsFeature) {
      const interval = setInterval(() => fetchMyDomains(true), 15000); // Poll every 15 seconds
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myDomains, api, featuresLoading, hasCustomDomainsFeature]);

  useEffect(() => {
    const clearMessages = (setter: React.Dispatch<React.SetStateAction<string | null>>) => {
      const timer = setTimeout(() => setter(null), 7000);
      return () => clearTimeout(timer);
    };
    if (searchError) clearMessages(setSearchError);
    if (byodError) clearMessages(setByodError);
    if (domainsError) clearMessages(setDomainsError);
    if (purchaseSuccess) clearMessages(setPurchaseSuccess);
  }, [searchError, byodError, domainsError, purchaseSuccess]);

  useEffect(() => {
    if (myDomains.length > 0 && !initialExpansionDone) {
      const initiallyExpanded = new Set<string>();
      myDomains.forEach(domain => {
        const statusInfo = getDomainStatusInfo(domain);
        const groupKey = statusInfo.group === 'failed' ? 'action_required' : statusInfo.group;
        if (groupKey !== 'action_required') {
          initiallyExpanded.add(domain.id);
        }
      });
      setExpandedDomains(initiallyExpanded);
      setInitialExpansionDone(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myDomains, initialExpansionDone]);

  const fetchMyDomains = async (silent = false) => {
    // Guard: Don't fetch if user doesn't have the feature
    if (!hasCustomDomainsFeature) {
      return;
    }

    if (!silent) setDomainsLoading(true);
    try {
      const response = await api.get<DomainListResponse>(apiEndpoints.domains.myDomains);
      // The backend already excludes tombstones; filter again so a regression there
      // can't put a dead row back in the list.
      setMyDomains((response.domains || []).filter(d => d.status !== 'deleted'));
      if (domainsError) setDomainsError(null);
    } catch (err: any) {
      console.error('Error fetching domains:', err);
      if (!silent) setDomainsError('Failed to load your domains. Please try again.');
    } finally {
      if (!silent) setDomainsLoading(false);
    }
  };

  // --- ACTION HANDLERS ---

  const setAction = (key: string, value: boolean) => setActionState(prev => ({ ...prev, [key]: value }));

  const confirmDeleteDomain = async () => {
    if (!deleteTarget || !api) return;
    const domain = deleteTarget;
    // actionState is keyed heterogeneously across this file; namespace the key so the
    // delete spinner and the reconcile spinner aren't the same bit.
    const key = `delete:${domain.id}`;

    setDeleteTarget(null);
    setAction(key, true);
    try {
      await api.delete(apiEndpoints.domains.domain(domain.id));
      // Flip locally so the row re-groups into "In Progress" and the poll starts on the
      // next render, rather than waiting for the refetch to come back.
      setMyDomains(prev => prev.map(d => (d.id === domain.id ? { ...d, status: 'deleting' as const } : d)));
      toast.success(`Deleting ${domain.domain_name}. This takes 15\u201320 minutes.`);
      await fetchMyDomains(true);
    } catch (err: any) {
      toast.error(friendlyDomainError(err, domain.domain_name, 'Failed to delete domain.'));
      // Re-sync: the row was probably stale (already deleting, or assigned elsewhere).
      await fetchMyDomains(true);
    } finally {
      setAction(key, false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim() || searching) return;

    setSearching(true);
    setSearchError(null);
    setSearchResults(null);
    try {
      const response = await api.get<DomainSearchResponse>(`${apiEndpoints.domains.search}?query=${encodeURIComponent(searchQuery.trim())}`);
      setSearchResults(response);
    } catch (err: any) {
      setSearchError(err.data?.detail || err.message || 'Failed to search domains.');
    } finally {
      setSearching(false);
    }
  };

  const handlePurchase = async (domainName: string) => {
    if (!api || actionState[domainName]) return;

    if (confirmState !== domainName) {
      setConfirmState(domainName);
      return;
    }

    setAction(domainName, true);
    setPurchaseSuccess(null);
    setSearchError(null);
    setConfirmState(null);
    try {
      const response = await api.post(apiEndpoints.domains.purchase, { domain_name: domainName });
      // CityPay redirects to the hosted payment page; Paddle opens an overlay and the
      // domain flips to PENDING via webhook once payment completes.
      await startCheckout(api, response, {
        onComplete: () => {
          setPurchaseSuccess('Payment received. Your domain registration is starting.');
          setAction(domainName, false);
          fetchMyDomains(true);
        },
      });
      if (response.checkout) {
        // Overlay checkout: re-enable the button in case the user dismisses the overlay.
        setAction(domainName, false);
      }
    } catch (err: any) {
      setSearchError(err.data?.detail || err.message || 'Failed to initiate purchase.');
      setAction(domainName, false);
    }
  };

  const handleReconcileDomain = async (domainId: string) => {
    if (!api || actionState[domainId]) return;

    setAction(domainId, true);
    setDomainsError(null);
    try {
      await api.post(apiEndpoints.domains.reconcile(domainId), {});
      setPurchaseSuccess('Domain reconciliation started. Refreshing status.');
      await fetchMyDomains(true);
    } catch (err: any) {
      setDomainsError(err.data?.detail || err.message || 'Failed to reconcile domain.');
    } finally {
      setAction(domainId, false);
    }
  };

  const handleBYODSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!byodDomain.trim() || actionState['byod']) return;

    // Validate domain before proceeding
    const validation = validateDomain(byodDomain);
    if (!validation.isValid) {
      setByodValidationError(validation.error || 'Invalid domain');
      return;
    }

    if (confirmState !== 'byod') {
      setConfirmState('byod');
      return;
    }

    setAction('byod', true);
    setPurchaseSuccess(null);
    setSearchError(null);
    setByodError(null);
    setByodValidationError(null);
    setConfirmState(null);
    try {
      // The backend normalizes subdomain input (www.example.com) to the root domain,
      // so report the domain it actually set up rather than what was typed.
      const result = await api.post(apiEndpoints.domains.byod, { domain_name: byodDomain.trim() });
      const addedDomain = result?.domain_name || byodDomain.trim();
      const normalizationNote = result?.normalized_from
        ? ` You entered ${result.normalized_from}; we connected the root domain, which covers subdomains like www automatically.`
        : '';
      setPurchaseSuccess(`${addedDomain} has been added!${normalizationNote} Configure your DNS settings as shown below.`);
      setBYODDomain('');
      await fetchMyDomains(true);
    } catch (err: any) {
      // Handle specific domain limit errors with better messaging
      if (err.status === 403 && err.data?.detail?.includes('Domain limit exceeded')) {
        setByodError(err.data.detail);
      } else {
        setByodError(err.data?.detail || err.message || 'Failed to add domain.');
      }
    } finally {
      setAction('byod', false);
    }
  };

  const toggleDomainExpanded = (domainId: string) => {
    setExpandedDomains(prev => {
      const newSet = new Set(prev);
      newSet.has(domainId) ? newSet.delete(domainId) : newSet.add(domainId);
      return newSet;
    });
  };

  // --- RENDER LOGIC ---

  // Always call hooks before any early returns (Rules of Hooks)
  const groupedDomains = useMemo(() => {
    return myDomains.reduce((groups, domain) => {
      const statusInfo = getDomainStatusInfo(domain);
      const groupKey = statusInfo.group === 'failed' ? 'action_required' : statusInfo.group;
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(domain);
      return groups;
    }, {} as Record<string, Domain[]>);
  }, [myDomains]);

  // Show upgrade prompt if feature is not available
  if (!featuresLoading && !hasCustomDomainsFeature) {
    return (
      <div className="space-y-8 pb-8">
        {/* Header */}
        <div>
          <h3 className="text-2xl font-bold text-neutral-900 dark:text-white">Domains</h3>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">Register new domains or connect existing ones to your projects.</p>
        </div>

        {/* Upgrade Prompt */}
        <FeatureUpgradePrompt
          icon={Globe}
          title="Upgrade to Access Custom Domains"
          description="Custom domains allow you to register new domains or connect existing ones to your projects."
        />
      </div>
    );
  }

  const domainGroupConfig: { [key in DomainStatusGroup]?: { title: string } } = {
    action_required: { title: 'Action Required' },
    ready: { title: 'Ready' },
    in_progress: { title: 'In Progress' },
  };
  
  const domainGroupOrder: DomainStatusGroup[] = ['ready', 'in_progress', 'action_required'];

  return (
    <div className="space-y-8 pb-8">
      {/* Header */}
      <div>
        <h3 className="text-2xl font-bold text-neutral-900 dark:text-white">Domains</h3>
        <p className="text-neutral-600 dark:text-neutral-400 mt-1">Register new domains or connect existing ones to your projects.</p>
      </div>

      {/* Search & BYOD */}
      <div className="space-y-6">
        {/* Domain Search */}
        <Card className="p-5">
          <h4 className="font-semibold text-neutral-900 dark:text-white">Search for a new domain</h4>
          <form onSubmit={handleSearch} className="mt-3 space-y-3" onMouseLeave={() => setConfirmState(null)}>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="e.g., my-awesome-idea"
                  className="w-full pl-9 pr-3 py-2 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm text-neutral-900 dark:text-white placeholder-neutral-500"
                />
              </div>
              <button type="submit" disabled={searching || !searchQuery.trim()} className="px-4 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:w-auto w-full">
                {searching ? <><Loader2 className="w-4 h-4 animate-spin" /> Searching</> : 'Search'}
              </button>
            </div>
          </form>

          {searchError && <InfoMessage type="error" message={searchError} />}
          {purchaseSuccess && <InfoMessage type="success" message={purchaseSuccess} />}

          {searchResults && (
            <div className="mt-4 space-y-2">
              {[searchResults.exact_match, ...searchResults.suggestions].filter(Boolean).slice(0, 6).map((domain) => (
                <div key={domain!.domain_name} className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-3 border border-neutral-200 dark:border-neutral-700/60 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-neutral-900 dark:text-white text-sm">{domain!.domain_name}</span>
                    {domain!.available ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-semantic-success/20 text-semantic-success">Available</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-semantic-error/20 text-semantic-error">Unavailable</span>
                    )}
                  </div>
                  {domain!.available && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                      {domain!.price && <span className="text-sm text-neutral-600 dark:text-neutral-400">${domain!.price}/yr</span>}
                      <button onClick={() => handlePurchase(domain!.domain_name)} disabled={actionState[domain!.domain_name]} className={`px-3 py-1.5 font-medium rounded-lg transition-colors text-xs flex-1 sm:flex-none sm:w-auto sm:min-w-24 flex-shrink-0 flex justify-center items-center ${confirmState === domain!.domain_name ? 'bg-semantic-warning/80 text-black hover:bg-semantic-warning' : 'bg-brand-primary text-black hover:bg-brand-primaryDark'}`}>
                        {actionState[domain!.domain_name] ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : (confirmState === domain!.domain_name ? 'Confirm' : 'Purchase')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Bring Your Own Domain */}
        <Card className="p-5">
          <h4 className="font-semibold text-neutral-900 dark:text-white">Connect an existing domain</h4>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">Already own a domain? Connect it for free.</p>
          <form onSubmit={handleBYODSubmit} className="mt-3" onMouseLeave={() => setConfirmState(null)}>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input 
                  type="text" 
                  value={byodDomain} 
                  onChange={(e) => {
                    setBYODDomain(e.target.value);
                    // Clear validation error on input change
                    if (byodValidationError) setByodValidationError(null);
                  }}
                  onBlur={() => {
                    // Validate on blur for immediate feedback
                    if (byodDomain.trim()) {
                      const validation = validateDomain(byodDomain);
                      if (!validation.isValid) {
                        setByodValidationError(validation.error || 'Invalid domain');
                      }
                    }
                  }}
                  placeholder="e.g., mysite.com" 
                  className={`w-full pl-9 pr-3 py-2 bg-white dark:bg-neutral-800 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm text-neutral-900 dark:text-white placeholder-neutral-500 ${byodValidationError ? 'border-semantic-error' : 'border-neutral-300 dark:border-neutral-600'}`}
                />
              </div>
              <button type="submit" disabled={actionState['byod'] || !byodDomain.trim()} className={`px-4 py-2 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm w-full sm:w-auto sm:min-w-32 flex-shrink-0 justify-center ${confirmState === 'byod' ? 'bg-semantic-warning/80 text-black hover:bg-semantic-warning' : 'bg-brand-primary text-black hover:bg-brand-primaryDark'}`}>
                {actionState['byod'] ? <><Loader2 className="w-4 h-4 animate-spin" /> Connecting</> : (confirmState === 'byod' ? 'Confirm' : 'Connect Domain')}
              </button>
            </div>
            {byodValidationError && (
              <p className="text-sm text-semantic-error mt-1 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {byodValidationError}
              </p>
            )}
          </form>
          
          {byodError && <InfoMessage type="error" message={byodError} />}
        </Card>
      </div>

      {/* My Domains */}
      <Card className="mb-8">
        <div className="p-5 flex items-center justify-between mb-2">
          <h4 className="text-lg font-semibold text-neutral-900 dark:text-white">My Domains ({myDomains.length})</h4>
          <button onClick={() => fetchMyDomains(false)} disabled={domainsLoading} className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-full transition-colors disabled:opacity-50" title="Refresh domains">
            <RefreshCw className={`w-4 h-4 text-neutral-500 ${domainsLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {domainsLoading && !myDomains.length ? (
          <div className="flex items-center justify-center py-16 text-neutral-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : domainsError ? (
          <div className="p-5">
            <InfoMessage type="error" message={domainsError} />
          </div>
        ) : myDomains.length === 0 ? (
          <div className="text-center py-16 text-neutral-500 dark:text-neutral-400 border-t border-neutral-200 dark:border-neutral-700/60">
            <Globe className="w-12 h-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" />
            <p className="font-medium">You don't have any domains yet.</p>
            <p className="text-sm mt-1">Search for a new domain or connect an existing one to get started.</p>
          </div>
        ) : (
          <div className="border-t border-neutral-200 dark:border-neutral-700/60">
            {domainGroupOrder.map(groupKey => (
              groupedDomains[groupKey] && groupedDomains[groupKey].length > 0 && (
                <div key={groupKey} className="last:pb-3">
                  <h5 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 px-5 py-2 bg-neutral-50 dark:bg-neutral-900/50 border-b border-t border-neutral-200 dark:border-neutral-700/60">{domainGroupConfig[groupKey]?.title}</h5>
                  {groupedDomains[groupKey].map(domain => (
                    <DomainListItem
                      key={domain.id}
                      domain={domain}
                      isExpanded={expandedDomains.has(domain.id)}
                      onToggleExpand={() => toggleDomainExpanded(domain.id)}
                      onReconcile={handleReconcileDomain}
                      onPurchase={handlePurchase}
                      onDelete={setDeleteTarget}
                      actionState={actionState}
                    />
                  ))}
                </div>
              )
            ))}
          </div>
        )}
      </Card>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        title="Delete this domain?"
        message={deleteTarget
          ? `${deleteTarget.domain_name} will be disconnected from Kolega. Its DNS zone, SSL certificate and CDN are removed, so any DNS records you added here \u2014 including email records \u2014 will stop working. This takes 15\u201320 minutes and cannot be undone; to use the domain again you would have to reconnect it and set its records up from scratch.`
          : ''}
        onConfirm={confirmDeleteDomain}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

const DomainListItem = ({ domain, isExpanded, onToggleExpand, onReconcile, onPurchase, onDelete, actionState }: {
  domain: Domain,
  isExpanded: boolean,
  onToggleExpand: () => void,
  onReconcile: (id: string) => void,
  onPurchase: (name: string) => void,
  onDelete: (domain: Domain) => void,
  actionState: { [key: string]: boolean }
}) => {
  const statusInfo = getDomainStatusInfo(domain);
  const isActionable = statusInfo.group === 'failed' || statusInfo.group === 'action_required';
  const [showDnsRecords, setShowDnsRecords] = useState(false);
  const canManageDns = !!domain.hosted_zone_id && (domain.status === 'registered' || domain.status === 'expiring_soon');
  // Only BYOD domains can be deleted: a purchased domain's Route53 registration is
  // paid for and cannot be released from here.
  const canDelete = domain.domain_type === 'byod' && domain.status !== 'deleting' && domain.status !== 'deleted';
  const isAssigned = !!domain.associated_workspace_id;
  const isDeleting = !!actionState[`delete:${domain.id}`];

  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (domain.status === 'payment_failed') {
      onPurchase(domain.domain_name);
    } else {
      onReconcile(domain.id);
    }
  };

  return (
    <div className="border-b border-neutral-200 dark:border-neutral-700/60 last:border-b-0">
      <div className="p-3 pl-5 pr-4 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors" onClick={onToggleExpand}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {isExpanded ? <ChevronDown className="w-4 h-4 text-neutral-500" /> : <ChevronRight className="w-4 h-4 text-neutral-500" />}
            <span className="font-medium text-neutral-900 dark:text-white truncate">{domain.domain_name}</span>
          </div>
          <div className="flex items-center self-stretch sm:self-auto justify-between sm:justify-end gap-4">
            <div className="flex items-center gap-2">
              {statusInfo.group === 'in_progress' ? <Loader2 className="w-4 h-4 text-brand-primary animate-spin" /> : statusInfo.icon}
              <span className={`text-sm font-medium ${statusInfo.color}`}>{statusInfo.text}</span>
            </div>
            <div className="flex items-center gap-2">
              {statusInfo.group === 'ready' && domain.status === 'registered' && (
                <a href={`https://${domain.domain_name}`} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-full" onClick={e => e.stopPropagation()} title="Open domain in new tab">
                  <ExternalLink className="w-4 h-4 text-neutral-500" />
                </a>
              )}
              {isActionable && (
                <button onClick={handleAction} disabled={actionState[domain.id] || actionState[domain.domain_name]} className="p-1.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-full" title={domain.status === 'payment_failed' ? 'Retry Payment' : 'Retry Setup'}>
                  {actionState[domain.id] || actionState[domain.domain_name] ? <Loader2 className="w-4 h-4 animate-spin text-brand-primary" /> : <RefreshCw className="w-4 h-4 text-neutral-500" />}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="bg-neutral-50 dark:bg-neutral-900/50 px-5 py-4">
          <div className="flex items-start gap-2 p-3 mb-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
            <Info className="w-4 h-4 text-neutral-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-neutral-600 dark:text-neutral-300">{statusInfo.description}</p>
          </div>

          {domain.domain_type === 'byod' && domain.name_servers && (
            <div className="mb-4">
              <h6 className="font-medium text-neutral-900 dark:text-white mb-2 text-sm">Nameserver Configuration</h6>
              <div className="space-y-1.5">
                {domain.name_servers.map((ns) => (
                  <div key={ns} className="flex items-center justify-between bg-white dark:bg-neutral-800 rounded-md px-3 py-1.5 border border-neutral-200 dark:border-neutral-700">
                    <code className="text-xs font-mono text-neutral-900 dark:text-white">{ns}</code>
                    <button onClick={() => copyToClipboard(ns)} className="p-1 text-neutral-500 hover:text-brand-primary" title="Copy nameserver"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2 text-sm">
            <div className="flex justify-between border-b border-dashed border-neutral-200 dark:border-neutral-700 py-1">
              <span className="font-medium text-neutral-600 dark:text-neutral-400">Type</span>
              <span className="text-neutral-900 dark:text-white">{domain.domain_type === 'byod' ? 'Bring Your Own' : 'Registered via Kolega'}</span>
            </div>
            {domain.purchased_at && (
              <div className="flex justify-between border-b border-dashed border-neutral-200 dark:border-neutral-700 py-1">
                <span className="font-medium text-neutral-600 dark:text-neutral-400">Registered</span>
                <span className="text-neutral-900 dark:text-white">{new Date(domain.purchased_at).toLocaleDateString()}</span>
              </div>
            )}
            {domain.expires_at && (
              <div className="flex justify-between border-b border-dashed border-neutral-200 dark:border-neutral-700 py-1">
                <span className="font-medium text-neutral-600 dark:text-neutral-400">Expires</span>
                <span className="text-neutral-900 dark:text-white">{new Date(domain.expires_at).toLocaleDateString()}</span>
              </div>
            )}
            {domain.associated_workspace && (
              <div className="flex justify-between border-b border-dashed border-neutral-200 dark:border-neutral-700 py-1">
                <span className="font-medium text-neutral-600 dark:text-neutral-400">Linked Project</span>
                <a href={`/project/${domain.associated_workspace.id}`} className="text-brand-primary hover:underline">{domain.associated_workspace.name}</a>
              </div>
            )}
          </div>

          {canManageDns && (
            <div className="mt-4">
              <button
                onClick={() => setShowDnsRecords(prev => !prev)}
                className="flex items-center gap-1.5 text-sm font-medium text-neutral-900 dark:text-white hover:text-brand-primary transition-colors"
              >
                {showDnsRecords ? <ChevronDown className="w-4 h-4 text-neutral-500" /> : <ChevronRight className="w-4 h-4 text-neutral-500" />}
                DNS Records
              </button>
              {showDnsRecords && (
                <div className="mt-3">
                  <DnsRecordsPanel domainId={domain.id} domainName={domain.domain_name} />
                </div>
              )}
            </div>
          )}

          {canDelete && (
            <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {isAssigned
                  ? 'Unassign this domain from its project before deleting it.'
                  : 'Removes the DNS zone, SSL certificate and CDN for this domain. This cannot be undone.'}
              </p>
              <button
                type="button"
                onClick={() => onDelete(domain)}
                disabled={isAssigned || isDeleting}
                title={isAssigned ? 'Unassign this domain from its project first' : 'Delete domain'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-semantic-error border border-semantic-error/30 hover:bg-semantic-error/10 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 self-start sm:self-auto"
              >
                {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Delete domain
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DomainsSection;
